import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Project } from "domain/entities/project";
import { ProjectInputFilter } from "../../domain/models/project-input-filter";
import { Logger } from "../../infra/logger/logger";
import projectRepository from "../../infra/repositories/project-repository";

export const useProjectsByFilter = (filter: ProjectInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["projects", filter],
    queryFn: async () => {
      try {
        const result = await projectRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useProjectsById = (id?: string) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      try {
        if (!id) throw new Error("Project ID inválido");
        const result = await projectRepository.getById(id);

        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata,
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    enabled: !!id,
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: Number(process.env.REACT_APP_QUERY_STALE_TIME_SHORT) || 0,
  });
};

export const useCreateProjectMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Project) => {
      const result = await projectRepository.create(data);
      if (result.metadata?.errors && result.metadata.errors.length > 0) {
        const err = new Error("Erro na criação do projeto");
        (err as any).response = { data: result };
        throw err;
      }
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
          console.log("error axios", errors);
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
        }
      } else if (error.response?.data?.metadata?.errors) {
        const errors = error.response.data.metadata.errors;
        logger.error(errors[0], error.response?.data.metadata);
      } else {
        logger.error("Erro desconhecido", error);
      }
    },
  });
};

export const useUpdateProjectMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Project) => {
      const result = await projectRepository.update(data);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata,
        );
      }
    },
  });
};

export const useDeleteProjectMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Project) => {
      if (!data || !data.id) throw new Error("Project ID inválido");
      const result = await projectRepository.delete(data.id);
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata.errors[0],
          error.response?.data.metadata,
        );
      }
    },
  });
};

export const useUpdateProjectStatusMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { id: string; status: string; justification: string }) => {
      const result = await projectRepository.updateStatus(data.id, data.status, data.justification);
      if (result.metadata?.errors && result.metadata.errors.length > 0) {
        const err = new Error("Erro ao atualizar o status do projeto");
        (err as any).response = { data: result };
        throw err;
      }
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["projects", {}] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        if (error.response?.data?.metadata?.errors) {
          const errors = error.response.data.metadata.errors;
          logger.error(errors[0], error.response?.data.metadata);
        } else {
          logger.error("Erro desconhecido da API", error.response?.data);
        }
      } else if (error.response?.data?.metadata?.errors) {
        const errors = error.response.data.metadata.errors;
        logger.error(errors[0], error.response?.data.metadata);
      } else {
        logger.error("Erro desconhecido", error);
      }
    },
  });
};
